import { HttpClient } from '@angular/common/http';
import { Injectable, } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { DisplayOptions } from '../../../core/display-options';
import { DisplayOptionsService } from '../../../core/display-options.service';
import { SessionData } from '../../../core/session-data';
import { SessionService } from '../../../core/session.service';
import { AccertifyService } from '../accertify.service';
import { CheckPriceData, CarDetail, VehicleCharge, CarRateCodeFee } from '../car-detail';
import { FeeTypes } from '../models/fee-types';
import { SearchCriteria } from '../search-types';
import { SharedSearchResultsService } from '../shared-search-results.service';
import { TimeService } from '../time.service';
import { Reservation, Price, ReservationResponse, ReservationRequest, InsuranceCoverage, Fee } from './reservation';

@Injectable()
export class ReservationService {
  public checkPriceData: CheckPriceData;
  public searchCriteria: SearchCriteria;
  public carDetail: CarDetail;
  public vehicleCharge: VehicleCharge;
  public currencyCode: string;

  public reservation: Reservation;
  private sessionData: SessionData;

  public carRateCodeFees: CarRateCodeFee[];
  public totalFees: Price;
  public dailyPrice: Price;
  public displayOptions: DisplayOptions;

  public hasChanged$ = new BehaviorSubject<boolean>(false);

  constructor(
    private httpClient: HttpClient,
    private sessionService: SessionService,
    private timeService: TimeService,
    private searchResultsService: SharedSearchResultsService,
    private displayOptionsService: DisplayOptionsService,
    private accertifyService: AccertifyService
  ) {
    this.sessionData = this.sessionService.getSessionData();
    this.displayOptions = this.displayOptionsService.getOptions();
    this.currencyCode = this.displayOptions.currency_code || 'USD';
  }

  public init() {
    this.reservation = new Reservation(this.currencyCode);
    this.populateReservation();
  }

  public remove() {
    this.reservation = undefined;
  }

  public get(): Reservation {
    if (!this.reservation) {
      this.init();
    }
    return this.reservation;
  }

  public notifyChange() {
    this.hasChanged$.next(true);
  }

  //Researve Car Here.
  public reserve(): Observable<ReservationResponse> {
    if (!this.reservation) {
      // TODO: Exception
      return EMPTY;
    }

    this.reservation.correlationId = this.sessionData.correlationId;

    //this.reservation.alternativeCurrenciesFees is used to setup the UI in case
    //fees are needed, however if no fees (points) are used it needs to be removed
    //from the reservation.
    if (this.reservation.alternativeCurrencies.length == 0) {
      this.reservation.alternativeCurrenciesFees = [];
    }

    if (this.reservation.alternativeCurrenciesFees.length > 0) {
      this.reservation.bookingFees = [];
    } else {
      this.reservation.bookingFees = this.checkPriceData.bookingFees;
    }

    if (this.reservation.pointsSubType === "noPoints") {
      this.reservation.pointsSubType = "";
    }

    if (this.carDetail.outsideAgentId) {
      this.reservation.outsideAgentId = this.carDetail.outsideAgentId;
    }

    this.reservation.insuranceCoverage = {
      ...this.reservation.insuranceCoverage,
      price: this.checkPriceData.insuranceCoverage.price,
      purchaseInsurance: this.checkPriceData.insuranceCoverage.purchaseInsurance
    };

    const reservationRequest = { ... this.reservation as ReservationRequest };

    reservationRequest.transactionId = this.accertifyService.transactionId;

    this.cleanUp(reservationRequest);

    return this.httpClient.post<ReservationResponse>(
      this.sessionData.apiUrl + '/reservation/reserve',
      reservationRequest);
  }

  private cleanUp(reservationRequest: ReservationRequest) {
    const extraKeys =
      this.getExtraKeys(new ReservationRequest(), new Reservation(this.currencyCode));
    this.omit(reservationRequest, extraKeys);
  }

  private omit(obj: any, keys: string[]) {
    keys.forEach(x => {
      if (obj.hasOwnProperty(x)) {
        obj[x] = undefined;
      }
    });
  }

  private getExtraKeys<T1, T2>(baseObj: T1, extendedObj: T2) {
    const baseKeys = Object.keys(baseObj);
    const allKeys = Object.keys(extendedObj);
    const extraKeys = allKeys.filter(function (el) {
      return !baseKeys.includes(el);
    });
    return extraKeys;
  }

  private populateReservation(): void {
    this.carDetail = this.searchResultsService.getCarDetail();
    this.searchCriteria = this.searchResultsService.searchCriteria;
    this.checkPriceData = this.searchResultsService.getPrice();

    if (this.checkPriceData) {
      this.checkPriceData.insuranceCoverage = new InsuranceCoverage();
    }

    this.initPriceData();

    if (this.carDetail) {
      this.populateWithCarDetail();
    }

    if (this.searchCriteria) {
      this.populateWithSearchCriteria();
    }

    if (this.checkPriceData) {
      this.populateWithPriceData();
      if (this.checkPriceData.vehicleCharges)
        this.vehicleCharge = this.checkPriceData.vehicleCharges.find(e => e.totalCharge.currencyCode === this.currencyCode);
    }
  }

  private populateWithCarDetail(): void {
    this.reservation.programName = this.displayOptions.program_short_name.toLowerCase();
    this.reservation.loyaltyInfo.programId = this.carDetail.vendorCode;
    this.reservation.vendorCode = this.carDetail.vendorCode;
    this.reservation.rateCode = this.carDetail.rateCode;
    this.reservation.ratePlan = this.carDetail.ratePlan;
    this.reservation.vehicleType = this.carDetail.vehicleType;
    this.reservation.pickUpLocation = this.carDetail.pickUpLocation;
    this.reservation.returnLocation = this.carDetail.returnLocation;
    this.reservation.locationCoordinate = this.carDetail.pickUpLocation.latitude + ':' + this.carDetail.pickUpLocation.longitude;
    this.reservation.paymentOptions = this.carDetail.paymentOptions;
    this.reservation.pointsSubType = this.carDetail.pointsSubType;
    this.reservation.outsideAgentId = this.carDetail.outsideAgentId;
    this.reservation.rateKey = this.carDetail.rateKey;
    this.reservation.netRateKey = this.carDetail.netRateKey;
    this.reservation.bookingKey = this.checkPriceData.bookingKey;
    this.reservation.imageUrl = this.carDetail.imageUrl;
    this.reservation.carModelName = this.carDetail.carModelName;
    this.reservation.companyLogoUrl = this.carDetail.companyLogoUrl;
    this.reservation.companyName = this.carDetail.companyName;
    this.reservation.numberDoors = this.carDetail.numberDoors;
    this.reservation.numberLargeBags = this.carDetail.numberLargeBags;
    this.reservation.numberSeats = this.carDetail.numberSeats;
  }

  private populateWithSearchCriteria(): void {
    this.reservation.pickUpTime = this.timeService.formatTime(this.searchCriteria.pickUp.time);
    this.reservation.pickUpDate = this.timeService.formatDate(this.searchCriteria.pickUp.date);
    this.reservation.returnTime = this.timeService.formatTime(this.searchCriteria.dropOff.time);
    this.reservation.returnDate = this.timeService.formatDate(this.searchCriteria.dropOff.date);
  }

  private populateWithPriceData(): void {

    if (!this.reservation) return;

    this.reservation.totalAmount = {
      amount: this.vehicleCharge.totalCharge && this.vehicleCharge.totalCharge.amount,
      currencyCode: this.vehicleCharge.totalCharge && this.vehicleCharge.totalCharge.currencyCode,
    };

    this.reservation.bookingFees = this.checkPriceData && this.checkPriceData.bookingFees.filter(bf => bf.fee && bf.fee.currencyCode === this.currencyCode);

    this.reservation.alternativeCurrenciesFees = this.checkPriceData && this.checkPriceData.alternativeCurrenciesFees.filter(bf => bf.fee && bf.fee.currencyCode === this.currencyCode);

    this.reservation.hash = this.checkPriceData && this.checkPriceData.hash;
  }

  //Return total of car booking.
  public getSubtotal(): Price {

    if (this.reservation) {
      var t = this.reservation.totalAmount;
      return t;
    }

    return { amount: 0, currencyCode: this.currencyCode };
  }

  //return total plus fees plus altcurrencies fees plus booking fees minus altcurrencies points or dbc + overidesfees.
  public getTotal(): Price {
    if (this.reservation)
      return this.reservation.getTotal();

    return { amount: 0, currencyCode: this.currencyCode };
  }

  public onPriceChange(newPrice: CheckPriceData) {
    this.searchResultsService.setPrice(newPrice);
    this.checkPriceData = newPrice;

  }

  public get processingFee(): Fee {
    return this.reservation.alternativeCurrenciesFees.find(f => f.type === FeeTypes.ProcessingFee);
  }
  public set processingFee(fee: Fee) {
    this.processingFee = fee;
  }

  private initPriceData(): void {
    if (!this.checkPriceData) {
      return;
    }

    if (this.checkPriceData.vehicleCharges) {
      this.vehicleCharge = this.checkPriceData.vehicleCharges
        .find(e => e.totalCharge.currencyCode === this.currencyCode);
    }

    if (this.checkPriceData.carRateCodeFees) {
      this.carRateCodeFees = this.checkPriceData.carRateCodeFees
        .filter(e => e.fee.currencyCode === this.currencyCode);
    }

    if (this.checkPriceData.totalFees) {
      this.totalFees = this.checkPriceData.totalFees
        .find(e => e.currencyCode === this.currencyCode);
    }

    if (this.checkPriceData.dailyPrices) {
      this.dailyPrice = this.checkPriceData.dailyPrices
        .find(e => e.currencyCode === this.currencyCode);
    }
  }
}




